import { useBoolean, useMount, useRequest, useUpdateEffect } from "ahooks";
import {
  append,
  concat,
  difference,
  equals,
  filter,
  find,
  includes,
  isEmpty,
  isNil,
  map,
  reduce,
} from "ramda";
import { Key, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  getPublishScopesOrganizationApi,
  getPublishScopesPersonnelApi,
} from "@/services/api/dept-tutorials";
import { IPublishScopeOrganizationNode } from "@/types/dept-tutorials";
import { findChildrenNodesByKeys } from "@/utils/tree";

import { IPersonnelScopeModalProps } from "./index";
import {
  convertTree,
  findParentKeys,
  findParentKeysByKeys,
  getAllChildrenKeys,
  getAllNotStaffChildrenKeys,
  getExcludesNodes,
  getNodeByKey,
  searchInTree,
} from "./utils";

export enum TabEnum {
  OrganizationalStructure,
  StaffingStructure,
}

// 已選列表項：樹節點的精簡信息
export type SelectItemType = {
  // 節點key
  key: string;
  // 節點名稱
  name: string;
  // 節點類型，0 員工
  type: number;
  // 員工數
  staffsCount: number;
};

// 由樹節點構造已選列表項
const toSelectItem = (node: IPublishScopeOrganizationNode): SelectItemType => {
  return {
    key: node.key.toString(),
    name: isNil(node.title) ? "" : node.title?.toString(),
    type: node.type,
    staffsCount: node.type === 0 ? 1 : node.staffsCount,
  };
};

export const useStore = (props: IPersonnelScopeModalProps) => {
  const { onOk, onCancel, defaultCheckedNodes } = props;

  // 記錄默認選中節點是否已初始化
  const isDefaultAppliedRef = useRef(false);

  const { t } = useTranslation();

  const tabs = [
    {
      key: TabEnum.OrganizationalStructure,
      label: t("organizational_structure"),
    },
    {
      key: TabEnum.StaffingStructure,
      label: t("personnel_structure"),
    },
  ];

  const [activeTab, setactiveTab] = useState<Key>(
    TabEnum.OrganizationalStructure
  );

  // 搜索關鍵字
  const [searchValue, setSearchValue] = useState<string>("");

  // 搜索關鍵字的節點keys
  const [foundedKeys, setFoundedKeys] = useState<Key[]>();

  // 樹選中的key列表
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);

  // 樹展開的節點key列表
  const [userTreeExpandedKeys, setUserTreeExpandedKeys] = useState<Key[]>([]);

  // 是否自動展開父節點
  const [autoExpandSturctureParent, autoExpandSturctureParentActions] =
    useBoolean(true);

  const [sturctureStaffTreeData, setSturctureStaffTreeData] = useState<
    IPublishScopeOrganizationNode[]
  >([]);

  const [personnelTreeData, setPersonnelTreeData] = useState<
    IPublishScopeOrganizationNode[]
  >([]);

  // 選中的權限詳細信息
  const [selectedList, setSelectedList] = useState<SelectItemType[]>([]);

  // 樹加載狀態
  const [treeLoading, treeLoadingActions] = useBoolean();

  const currentTreeData = useMemo(() => {
    return activeTab === TabEnum.OrganizationalStructure
      ? sturctureStaffTreeData
      : personnelTreeData;
  }, [activeTab, sturctureStaffTreeData, personnelTreeData]);

  const getStructureRequest = useRequest(getPublishScopesOrganizationApi, {
    manual: true,
    onSuccess: (res) => {
      res.data && setSturctureStaffTreeData(convertTree(res.data));
    },
  });

  const getPersonnelTreeRequest = useRequest(getPublishScopesPersonnelApi, {
    manual: true,
    onSuccess: (res) => {
      res.data && setPersonnelTreeData(convertTree(res.data));
    },
  });

  // 初始化數據
  const initData = async () => {
    treeLoadingActions.setTrue();

    try {
      await Promise.all([
        getPersonnelTreeRequest.runAsync(),
        getStructureRequest.runAsync(1),
      ]);
    } finally {
      treeLoadingActions.setFalse();
    }
  };

  // 將默認選中節點賦值到已選列表和樹
  const applyDefaultCheckedNodes = () => {
    if (isNil(defaultCheckedNodes) || isEmpty(defaultCheckedNodes)) {
      return;
    }

    const newSelectedList: SelectItemType[] = [];

    const newSelectedKeys: Key[] = [];

    for (const item of defaultCheckedNodes) {
      const node = getNodeByKey(item.key, sturctureStaffTreeData);

      // 樹中不存在的節點也要顯示在已選列表中
      newSelectedList.push(item);

      if (node) {
        const childrenKeys = isNil(node.children)
          ? []
          : getAllChildrenKeys(node.children);

        // 勾選節點及其所有子節點，保持與手動勾選一致的顯示
        newSelectedKeys.push(...childrenKeys, node.key);
      }
    }
    setSelectedList(newSelectedList);
    setSelectedKeys(newSelectedKeys);
  };

  // 處理搜索
  const handleSearch = (value: string) => {
    (isEmpty(value) || isNil(value)) && setFoundedKeys(undefined);
    setSearchValue(value);
    // autoExpandSturctureParentActions.setTrue();
  };

  const handleChangeTab = (key: TabEnum) => {
    setactiveTab(key);
  };

  // 處理清除
  const handleClear = () => {
    setSelectedKeys([]);
    setSelectedList([]);
  };

  const getNodeAndChildrenKeys = (keys: Key[]) => {
    const selectedItemChildrenNodes = findChildrenNodesByKeys(
      keys,
      sturctureStaffTreeData
    );

    return concat(
      map((node) => node.key, selectedItemChildrenNodes),
      keys
    );
  };

  // 處理取消選中
  const handleCancelSelectedKey = (selectItem: SelectItemType) => {
    setSelectedKeys(
      difference(selectedKeys, getNodeAndChildrenKeys([selectItem.key]))
    );
    setSelectedList(
      filter((item) => !equals(item.key, selectItem.key), selectedList)
    );
  };

  // 處理樹節點選擇
  const handleTreeSelect = useCallback((checkedKeys: Key[]) => {
    setSelectedKeys(checkedKeys as string[]);
  }, []);

  /**
   * 處理樹節點展開/折疊事件
   * @param expandedKeys - 展開後的所有展開節點key列表
   * @param info - 觸發展開操作的節點信息
   */
  const handleUserTreeExpand = (expandedKeys: Key[]) => {
    // 更新展開的節點列表
    setUserTreeExpandedKeys(expandedKeys);
    // 關閉自動展開父節點功能（用戶手動展開後由用戶控制）
    autoExpandSturctureParentActions.setFalse();
  };

  const handleCheck = useCallback(
    (
      checked: {
        checked: Key[];
        halfChecked: Key[];
      },
      info: any
    ) => {
      const { checked: newCheckedKeys } = checked;

      const nodeKey = info.node.key;

      const sturctureNode = getNodeByKey(nodeKey, sturctureStaffTreeData);

      const personnelNode = getNodeByKey(nodeKey, personnelTreeData);

      // 判斷是選中還是取消選中
      const isChecking = newCheckedKeys.includes(nodeKey);

      if (!sturctureNode && personnelNode) {
        if (isChecking) {
          setSelectedList(append(toSelectItem(personnelNode), selectedList));

          setSelectedKeys(newCheckedKeys);
        } else {
          // 树选中的節點
          setSelectedKeys(newCheckedKeys);
          setSelectedList(
            filter((item) => {
              return !equals(item.key, nodeKey);
            }, selectedList)
          );
        }
      } else if (sturctureNode) {
        const allChildrenKeys = isNil(sturctureNode.children)
          ? []
          : getAllChildrenKeys(sturctureNode.children);

        const allFatherKeys = findParentKeys(nodeKey, sturctureStaffTreeData);

        if (isChecking) {
          if (TabEnum.OrganizationalStructure === activeTab) {
            // 获取已选列表中与选中节点的所有子节点无关的数据
            const excludeChildrenSelectedData = filter((item) => {
              return !includes(item.key, allChildrenKeys);
            }, selectedList);

            // 已选列表中包括选中节点的数据
            const newSelectedList = append(
              toSelectItem(sturctureNode),
              excludeChildrenSelectedData
            );

            setSelectedList(newSelectedList);

            // 2. 將所有key加入selectedKeys（子節點也選中以在Tree中顯示）
            const updatedKeys = [
              ...new Set([...checked.checked, ...allChildrenKeys]),
            ];

            setSelectedKeys(updatedKeys);
          } else {
            setSelectedList(append(toSelectItem(sturctureNode), selectedList));

            setSelectedKeys(newCheckedKeys);
          }
        } else {
          // 树选中的節點
          setSelectedKeys(
            filter((key) => {
              return (
                !includes(key, allFatherKeys) && !includes(key, allChildrenKeys)
              );
            }, newCheckedKeys)
          );

          // 获取取消勾选的节点在已选列表中的父节点
          const selectdUnit = find((item) => {
            return includes(item.key, allFatherKeys);
          }, selectedList);

          if (selectdUnit) {
            // 获取该父节点
            const selectedNode = getNodeByKey(
              selectdUnit.key,
              sturctureStaffTreeData
            );

            if (selectedNode?.children) {
              // 获取该父节点下非取消节点的所有子节点
              const showNodes = getExcludesNodes(
                selectedNode.children,
                allFatherKeys,
                nodeKey
              ) as IPublishScopeOrganizationNode[];

              // 获取已选列表中跟取消节点无关的数据
              const noCancelSelectedData = filter((item) => {
                return !includes(item.key, allFatherKeys);
              }, selectedList);

              // 赋值
              setSelectedList(
                concat(noCancelSelectedData, map(toSelectItem, showNodes))
              );
            }
          } else {
            setSelectedList(
              filter((item) => {
                return !equals(item.key, nodeKey);
              }, selectedList)
            );
          }
        }
      }
    },
    [sturctureStaffTreeData, personnelTreeData, selectedList, activeTab]
  );

  const handleCancel = () => {
    onCancel();
    setSearchValue("");
  };

  const handleOk = () => {
    onOk(
      map((item) => {
        return { ...item, key: item.key.toString() };
      }, selectedList)
    );
  };

  const selectedPeopleCount = useMemo(() => {
    return reduce(
      (count: number, item: SelectItemType) => count + (item.staffsCount ?? 0),
      0,
      selectedList
    );
  }, [selectedList]);

  useMount(() => {
    initData();
  });

  // 樹數據加載完成後，將默認選中節點賦值到列表和樹（僅執行一次）
  useUpdateEffect(() => {
    if (!isDefaultAppliedRef.current && sturctureStaffTreeData.length > 0) {
      isDefaultAppliedRef.current = true;

      applyDefaultCheckedNodes();
    }
  }, [sturctureStaffTreeData]);

  useUpdateEffect(() => {
    if (!isNil(searchValue) && !isEmpty(searchValue)) {
      const isStructureTab = activeTab === TabEnum.OrganizationalStructure;

      const isFound = searchInTree(
        isStructureTab ? sturctureStaffTreeData : personnelTreeData,
        searchValue
      );

      if (!isFound) {
        const foundAgain = searchInTree(
          isStructureTab ? personnelTreeData : sturctureStaffTreeData,
          searchValue
        );

        foundAgain &&
          handleChangeTab(
            isStructureTab
              ? TabEnum.StaffingStructure
              : TabEnum.OrganizationalStructure
          );
      }
    }
  }, [searchValue]);

  useUpdateEffect(() => {
    if (!isNil(foundedKeys)) {
      const parentKeys = findParentKeysByKeys(foundedKeys, currentTreeData);

      Array.isArray(parentKeys) &&
        setUserTreeExpandedKeys((prev) => concat(prev, parentKeys));
    }
  }, [foundedKeys]);

  return {
    // 狀態
    t,
    tabs,
    selectedKeys,
    searchValue,
    currentTreeData,
    treeLoading,
    activeTab,
    selectedList,
    userTreeExpandedKeys,
    autoExpandSturctureParent,
    foundedKeys,
    selectedPeopleCount,

    // 操作
    setSearchValue,
    handleSearch,
    handleClear,
    handleCancelSelectedKey,
    handleTreeSelect,
    handleChangeTab,
    handleCheck,
    handleUserTreeExpand,
    setFoundedKeys,
    handleCancel,
    handleOk,
  };
};
